import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SwingPlayer from '../components/SwingPlayer';
import VideoPicker from '../components/VideoPicker';
import DrawToolbar from '../components/DrawToolbar';
import { api } from '../lib/api';

const SPEEDS = [0.1, 0.25, 0.5, 1];

export default function Analyzer() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();

  const [videoA, setVideoA] = useState(null);       // { r2_key, url, label, ... } or null
  const [videoB, setVideoB] = useState(null);
  const [syncA, setSyncA] = useState(null);
  const [syncB, setSyncB] = useState(null);
  const [endA, setEndA] = useState(null);           // loop stop point — when hit, both jump back to sync
  const [endB, setEndB] = useState(null);
  const [drawingsA, setDrawingsA] = useState([]);
  const [drawingsB, setDrawingsB] = useState([]);

  const [playing, setPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [trimA, setTrimA] = useState(1);     // per-video rate multiplier for manual fine-tune
  const [trimB, setTrimB] = useState(1);
  const [activeTool, setActiveTool] = useState('none');
  const [activeColor, setActiveColor] = useState('#f4a948');

  const [sessionName, setSessionName] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [loadedSessionId, setLoadedSessionId] = useState(sessionId || null);

  const [pickerOpen, setPickerOpen] = useState(null); // 'A' | 'B' | null
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [showMore, setShowMore] = useState(false);    // mobile: reveal trim/session/hints

  const playerARef = useRef(null);
  const playerBRef = useRef(null);

  // ---------------------------------------------------------------------
  // Load session if we're editing one
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const s = await api.getSession(sessionId);
        setSessionName(s.name);
        setSessionNotes(s.notes || '');
        setSyncA(s.sync_a);
        setSyncB(s.sync_b);
        setDrawingsA(s.drawings_a || []);
        setDrawingsB(s.drawings_b || []);
        setLoadedSessionId(s.id);
        if (s.video_a_key) {
          setVideoA({ r2_key: s.video_a_key, url: api.streamUrl(s.video_a_key), label: 'Swing A' });
        }
        if (s.video_b_key) {
          setVideoB({ r2_key: s.video_b_key, url: api.streamUrl(s.video_b_key), label: 'Swing B' });
        }
      } catch (err) {
        console.error('Failed to load session', err);
      }
    })();
  }, [sessionId]);

  // ---------------------------------------------------------------------
  // Query param shortcuts: /analyze?a=<r2_key>&b=<r2_key>
  // Handy for "compare to pro" flow from the library page
  // ---------------------------------------------------------------------
  useEffect(() => {
    const a = searchParams.get('a');
    const b = searchParams.get('b');
    if (a) setVideoA({ r2_key: a, url: api.streamUrl(a), label: 'Swing A' });
    if (b) setVideoB({ r2_key: b, url: api.streamUrl(b), label: 'Swing B' });
  }, [searchParams]);

  // ---------------------------------------------------------------------
  // Tempo matching
  //
  // When both videos have BOTH sync (takeaway) and end (finish) markers,
  // we enter "tempo-matched" mode. The two windows are treated as the
  // same phase — 0% at sync, 100% at end — and playback rates are
  // scaled so both videos traverse their window in the same wall-clock
  // time. The faster-tempo swing slows down to match the slower one.
  //
  // Outside the window (before sync, after end), time falls back to a
  // simple offset so the full videos are still navigable.
  // ---------------------------------------------------------------------
  // Memoize so the object identity is stable across renders where the
  // inputs haven't changed — otherwise every downstream useCallback that
  // depends on `tempoMatch` rebuilds, and the applyRates useEffect fires
  // spuriously every render.
  const tempoMatch = useMemo(() => {
    if (syncA == null || syncB == null || endA == null || endB == null) return null;
    const windowA = endA - syncA;
    const windowB = endB - syncB;
    if (windowA <= 0 || windowB <= 0) return null;
    const reference = Math.max(windowA, windowB);
    return {
      windowA, windowB, reference,
      rateA: windowA / reference,   // ≤ 1
      rateB: windowB / reference    // ≤ 1
    };
  }, [syncA, syncB, endA, endB]);

  // Map a time on video A to the equivalent phase-matched time on B.
  // Inside the window: same percentage through.
  // Outside: preserve wall-clock offset relative to the nearest anchor.
  const aTimeToB = useCallback((ta) => {
    if (syncA == null || syncB == null) return null;
    if (tempoMatch) {
      if (ta >= syncA && ta <= endA) {
        const pct = (ta - syncA) / tempoMatch.windowA;
        return syncB + pct * tempoMatch.windowB;
      }
      // Before sync or after end: fall back to offset from nearest anchor
      if (ta < syncA) return syncB + (ta - syncA);
      return endB + (ta - endA);
    }
    return syncB + (ta - syncA);
  }, [syncA, syncB, endA, endB, tempoMatch]);

  const bTimeToA = useCallback((tb) => {
    if (syncA == null || syncB == null) return null;
    if (tempoMatch) {
      if (tb >= syncB && tb <= endB) {
        const pct = (tb - syncB) / tempoMatch.windowB;
        return syncA + pct * tempoMatch.windowA;
      }
      if (tb < syncB) return syncA + (tb - syncB);
      return endA + (tb - endB);
    }
    return syncA + (tb - syncB);
  }, [syncA, syncB, endA, endB, tempoMatch]);

  // `aligningRef` absorbs the echo: when alignB seeks B, B's frame loop
  // fires and would otherwise call alignA right back, causing a feedback
  // loop. The flag + short timeout lets the seek settle.
  const aligningRef = useRef(false);

  const alignB = useCallback(() => {
    if (aligningRef.current) return;
    const a = playerARef.current;
    const b = playerBRef.current;
    if (!a || !b) return;
    const target = aTimeToB(a.getTime());
    if (target == null) return;
    if (Math.abs(b.getTime() - target) > 0.02) {
      aligningRef.current = true;
      b.seek(target);
      setTimeout(() => { aligningRef.current = false; }, 120);
    }
  }, [aTimeToB]);

  const alignA = useCallback(() => {
    if (aligningRef.current) return;
    const a = playerARef.current;
    const b = playerBRef.current;
    if (!a || !b) return;
    const target = bTimeToA(b.getTime());
    if (target == null) return;
    if (Math.abs(a.getTime() - target) > 0.02) {
      aligningRef.current = true;
      a.seek(target);
      setTimeout(() => { aligningRef.current = false; }, 120);
    }
  }, [bTimeToA]);

  // Apply per-video playback rate. Stacking order:
  //   effective = global playbackRate  ×  tempoMatch.rate (auto match)
  //                                    ×  user trim (manual fine-tune)
  // Trims let you nudge each video to get the two swings perfectly in
  // phase when the auto-match is slightly off.
  const applyRates = useCallback(() => {
    const pa = playerARef.current;
    const pb = playerBRef.current;
    if (!pa || !pb) return;
    const matchA = tempoMatch ? tempoMatch.rateA : 1;
    const matchB = tempoMatch ? tempoMatch.rateB : 1;
    pa.setPlaybackRate(playbackRate * matchA * trimA);
    pb.setPlaybackRate(playbackRate * matchB * trimB);
  }, [playbackRate, tempoMatch, trimA, trimB]);

  const playBoth = () => {
    if (!videoA || !videoB) return;
    alignB();
    applyRates();
    playerARef.current?.play();
    playerBRef.current?.play();
    setPlaying(true);
  };

  // If sync/end markers change while playing, update the per-video rates live
  useEffect(() => {
    if (playing) applyRates();
  }, [playing, applyRates]);

  const pauseBoth = () => {
    playerARef.current?.pause();
    playerBRef.current?.pause();
    setPlaying(false);
  };

  // Reset goes to each video's sync mark (or 0 if not set). No run-up.
  const resetBoth = () => {
    pauseBoth();
    playerARef.current?.seek(syncA ?? 0);
    playerBRef.current?.seek(syncB ?? 0);
  };

  // Loop back to sync while keeping playback going.
  //
  // The flag is CLEARED by the time-change callbacks when they see a
  // post-seek time (close to sync), not by a fixed timeout — because on
  // mobile, seeks can take longer than a timer to settle, and a stale
  // frame callback firing with the old high time would otherwise trigger
  // a phantom second loop. A long safety timeout unsticks the flag if
  // seeks never land for some reason.
  const loopingRef = useRef(false);
  const loopToSync = useCallback(() => {
    if (loopingRef.current) return;
    const targetA = syncA ?? 0;
    const targetB = syncB ?? 0;
    // If either sync is at/after its end, looping would thrash — bail.
    if ((endA != null && targetA >= endA) || (endB != null && targetB >= endB)) return;
    loopingRef.current = true;
    playerARef.current?.seek(targetA);
    playerBRef.current?.seek(targetB);
    // Safety net — in case seeked events never propagate
    setTimeout(() => { loopingRef.current = false; }, 1500);
  }, [syncA, syncB, endA, endB]);

  // Step one frame on each. When tempo-matched, step A first then snap B
  // to the phase-equivalent time so stepping preserves the match. While
  // still placing markers (or fully unsynced), keep stepping independent.
  const stepBoth = (dir) => {
    pauseBoth();
    const a = playerARef.current;
    const b = playerBRef.current;
    if (!a || !b) return;
    a.stepFrame(dir);
    if (tempoMatch) {
      aligningRef.current = true;
      const target = aTimeToB(a.getTime());
      if (target != null) b.seek(target);
      setTimeout(() => { aligningRef.current = false; }, 120);
    } else {
      b.stepFrame(dir);
    }
  };

  // Master "Set sync" — captures each player's current time as its sync.
  // Workflow: scrub each video to impact, then click once.
  const setSyncBoth = () => {
    const a = playerARef.current?.getTime();
    const b = playerBRef.current?.getTime();
    if (a != null) setSyncA(a);
    if (b != null) setSyncB(b);
  };
  const clearSyncBoth = () => { setSyncA(null); setSyncB(null); };

  // Master "Set end" — same idea, for the loop stop point.
  const setEndBoth = () => {
    const a = playerARef.current?.getTime();
    const b = playerBRef.current?.getTime();
    if (a != null) setEndA(a);
    if (b != null) setEndB(b);
  };
  const clearEndBoth = () => { setEndA(null); setEndB(null); };

  // Scrub coupling rule: only move the other video while paused when BOTH
  // sync and end markers are set (i.e. we're tempo-matched and reviewing).
  // While you're still placing markers (sync set, end not yet), each
  // timeline must scrub independently — otherwise moving A to its finish
  // frame drags B off its own finish, and "Set end" ends up capturing
  // identical windows on both videos.
  //
  // While playing: check the end marker and loop back to sync if hit.
  // While inside a loop: watch for the post-seek time landing back near
  // sync and clear the guard then, so we don't have to rely on a timeout.
  const onATimeChange = useCallback((time) => {
    if (!playing) {
      if (tempoMatch) alignB();
      return;
    }
    if (loopingRef.current) {
      if (syncA != null && time < syncA + 0.1) loopingRef.current = false;
      return;
    }
    if (endA != null && time >= endA) loopToSync();
  }, [playing, tempoMatch, alignB, endA, syncA, loopToSync]);

  const onBTimeChange = useCallback((time) => {
    if (!playing) {
      if (tempoMatch) alignA();
      return;
    }
    if (loopingRef.current) {
      if (syncB != null && time < syncB + 0.1) loopingRef.current = false;
      return;
    }
    if (endB != null && time >= endB) loopToSync();
  }, [playing, tempoMatch, alignA, endB, syncB, loopToSync]);

  // ---------------------------------------------------------------------
  // Keyboard shortcuts
  // ---------------------------------------------------------------------
  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (e.code === 'Space') { e.preventDefault(); playing ? pauseBoth() : playBoth(); }
      else if (e.key === ',') stepBoth(-1);
      else if (e.key === '.') stepBoth(1);
      else if (e.key === 'r' || e.key === 'R') resetBoth();
      else if (e.key === '1') setActiveTool('none');
      else if (e.key === '2') setActiveTool('line');
      else if (e.key === '3') setActiveTool('circle');
      else if (e.key === '4') setActiveTool('angle');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playing, videoA, videoB, syncA, syncB]);

  // ---------------------------------------------------------------------
  // Save session
  // ---------------------------------------------------------------------
  const canSave = videoA && videoB && sessionName.trim();

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await api.saveSession({
        id: loadedSessionId,
        name: sessionName.trim(),
        notes: sessionNotes,
        video_a_key: videoA.r2_key,
        video_b_key: videoB.r2_key,
        sync_a: syncA,
        sync_b: syncB,
        drawings_a: drawingsA,
        drawings_b: drawingsB
      });
      setLoadedSessionId(res.id);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2000);
      if (!sessionId) navigate(`/analyze/${res.id}`, { replace: true });
    } catch (err) {
      setSaveMsg('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const syncStatus = syncA != null && syncB != null
    ? 'synced'
    : (syncA != null || syncB != null) ? 'partial' : 'none';

  return (
    <div className="max-w-[1400px] mx-auto p-2 md:p-4 space-y-3 md:space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between pb-2 md:pb-3 border-b border-fairway-800">
        <div>
          <h1 className="display text-lg md:text-2xl font-medium text-cream-100">
            {loadedSessionId ? 'Session' : 'New comparison'}
          </h1>
          <p className="hidden md:block text-xs text-ink-400 mt-0.5">
            Two swings, side by side. Mark takeaway (sync) and finish (end) on each — tempos match automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/sessions')}
            className="px-3 py-1.5 text-xs uppercase tracking-wider text-ink-300 hover:text-ink-100 transition"
          >
            ← Sessions
          </button>
        </div>
      </div>

      {/* Dual players — always side-by-side. On mobile each card fills
          half the viewport width; on lg they size by height-derived
          aspect and center as a group. */}
      <div className="flex flex-row justify-center items-start gap-2 lg:gap-4">
        <div className="flex flex-col items-center flex-1 lg:flex-initial min-w-0">
          {videoA ? (
            <SwingPlayer
              src={videoA.url}
              label={`Swing A${videoA.label && videoA.label !== 'Swing A' ? ` · ${videoA.label}` : ''}`}
              innerRef={playerARef}
              syncTime={syncA}
              endTime={endA}
              drawings={drawingsA}
              onDrawingsChange={setDrawingsA}
              activeTool={activeTool}
              activeColor={activeColor}
              playbackRate={playbackRate}
              onTimeChange={onATimeChange}
            />
          ) : (
            <EmptySlot label="Swing A" onPick={() => setPickerOpen('A')} />
          )}
          <div className="mt-2">
            <button
              onClick={() => setPickerOpen('A')}
              className="text-[11px] text-ink-400 hover:text-ink-200 uppercase tracking-wider"
            >
              {videoA ? 'Replace video' : 'Pick video'}
            </button>
          </div>
        </div>

        <div className="flex flex-col items-center flex-1 lg:flex-initial min-w-0">
          {videoB ? (
            <SwingPlayer
              src={videoB.url}
              label={`Swing B${videoB.label && videoB.label !== 'Swing B' ? ` · ${videoB.label}` : ''}`}
              innerRef={playerBRef}
              syncTime={syncB}
              endTime={endB}
              drawings={drawingsB}
              onDrawingsChange={setDrawingsB}
              activeTool={activeTool}
              activeColor={activeColor}
              playbackRate={playbackRate}
              onTimeChange={onBTimeChange}
            />
          ) : (
            <EmptySlot label="Swing B" onPick={() => setPickerOpen('B')} />
          )}
          <div className="mt-2">
            <button
              onClick={() => setPickerOpen('B')}
              className="text-[11px] text-ink-400 hover:text-ink-200 uppercase tracking-wider"
            >
              {videoB ? 'Replace video' : 'Pick video'}
            </button>
          </div>
        </div>
      </div>

      {/* Master controls */}
      <div className="bg-fairway-900 border border-fairway-800 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="hidden md:inline text-[10px] uppercase tracking-[0.2em] text-ink-400 mr-1">Playback</span>
          <button
            onClick={() => playing ? pauseBoth() : playBoth()}
            disabled={!videoA || !videoB}
            className="px-5 md:px-4 py-3 md:py-2 bg-cream-100 text-fairway-950 rounded font-medium text-base md:text-sm hover:bg-cream-200 disabled:opacity-30 disabled:cursor-not-allowed transition min-h-[44px]"
          >
            {playing ? '⏸' : '▶'}<span className="hidden md:inline ml-1">{playing ? ' Pause both' : ' Play both'}</span>
          </button>
          <button
            onClick={resetBoth}
            disabled={!videoA || !videoB}
            className="px-4 md:px-3 py-3 md:py-2 bg-fairway-800 text-ink-100 rounded text-sm md:text-xs uppercase tracking-wider hover:bg-fairway-700 disabled:opacity-30 transition min-h-[44px] md:min-h-0"
          >
            ⏮<span className="hidden md:inline ml-1">Reset</span>
          </button>
          <button
            onClick={() => stepBoth(-1)}
            disabled={!videoA || !videoB}
            className="px-4 md:px-3 py-3 md:py-2 bg-fairway-800 text-ink-100 rounded text-sm md:text-xs uppercase tracking-wider hover:bg-fairway-700 disabled:opacity-30 transition font-mono min-h-[44px] md:min-h-0"
          >
            ⟨<span className="hidden md:inline ml-1">Frame</span>
          </button>
          <button
            onClick={() => stepBoth(1)}
            disabled={!videoA || !videoB}
            className="px-4 md:px-3 py-3 md:py-2 bg-fairway-800 text-ink-100 rounded text-sm md:text-xs uppercase tracking-wider hover:bg-fairway-700 disabled:opacity-30 transition font-mono min-h-[44px] md:min-h-0"
          >
            <span className="hidden md:inline mr-1">Frame</span>⟩
          </button>

          <div className="flex items-center gap-1 ml-2">
            {SPEEDS.map(s => (
              <button
                key={s}
                onClick={() => {
                  setPlaybackRate(s);
                  // applyRates reads playbackRate from state; use a microtask
                  // so the state update is visible when we apply.
                  if (playing) setTimeout(() => applyRates(), 0);
                }}
                className={`px-2.5 py-1.5 rounded text-[11px] font-mono transition ${
                  playbackRate === s
                    ? 'bg-cream-100 text-fairway-950'
                    : 'bg-fairway-800 text-ink-200 hover:bg-fairway-700'
                }`}
              >
                {s}×
              </button>
            ))}
          </div>

          <div className="flex-1" />

          <SyncBadge status={syncStatus} syncA={syncA} syncB={syncB} tempoMatch={tempoMatch} />
        </div>

        {/* Sync + loop-end controls — one button pair sets/clears for BOTH videos */}
        <div className="pt-2 border-t border-fairway-800 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.2em] text-ink-400 mr-1">Markers</span>

          <button
            onClick={setSyncBoth}
            disabled={!videoA || !videoB}
            title="Capture each video's current frame as its sync point"
            className="px-3 py-1.5 rounded text-[11px] uppercase tracking-wider bg-accent-flag/10 border border-accent-flag/40 text-accent-flag hover:bg-accent-flag/20 disabled:opacity-30 transition"
          >
            ● Set sync
          </button>
          <button
            onClick={clearSyncBoth}
            disabled={syncA == null && syncB == null}
            className="px-3 py-1.5 rounded text-[11px] uppercase tracking-wider text-ink-300 hover:text-ink-100 hover:bg-fairway-800 disabled:opacity-30 transition"
          >
            Clear sync
          </button>
          {(syncA != null || syncB != null) && (
            <span className="hidden md:inline monospace text-[10px] text-ink-400">
              A{syncA != null ? ` ${syncA.toFixed(2)}s` : ' —'} · B{syncB != null ? ` ${syncB.toFixed(2)}s` : ' —'}
            </span>
          )}

          <span className="w-px h-5 bg-fairway-800 mx-1" />

          <button
            onClick={setEndBoth}
            disabled={!videoA || !videoB}
            title="Capture each video's current frame as its loop end"
            className="px-3 py-1.5 rounded text-[11px] uppercase tracking-wider bg-cream-300/10 border border-cream-300/40 text-cream-300 hover:bg-cream-300/20 disabled:opacity-30 transition"
          >
            ▮ Set end
          </button>
          <button
            onClick={clearEndBoth}
            disabled={endA == null && endB == null}
            className="px-3 py-1.5 rounded text-[11px] uppercase tracking-wider text-ink-300 hover:text-ink-100 hover:bg-fairway-800 disabled:opacity-30 transition"
          >
            Clear end
          </button>
          {(endA != null || endB != null) && (
            <span className="hidden md:inline monospace text-[10px] text-ink-400">
              A{endA != null ? ` ${endA.toFixed(2)}s` : ' —'} · B{endB != null ? ` ${endB.toFixed(2)}s` : ' —'}
            </span>
          )}
        </div>

        {/* Mobile "More" toggle — reveals the advanced rows below. On
            md+ everything is always visible. */}
        <div className="md:hidden">
          <button
            onClick={() => setShowMore(s => !s)}
            className="w-full py-2 rounded text-[11px] uppercase tracking-[0.2em] text-ink-300 hover:text-ink-100 hover:bg-fairway-800 border border-fairway-800 transition"
          >
            {showMore ? '▲ Hide advanced' : '▼ More controls'}
          </button>
        </div>

        {/* Per-video speed trim — fine-tune the auto tempo match */}
        <div className={`${showMore ? 'flex' : 'hidden'} md:flex pt-2 border-t border-fairway-800 items-center gap-2 flex-wrap`}>
          <span className="text-[10px] uppercase tracking-[0.2em] text-ink-400 mr-1">Trim</span>

          <TrimControl
            label="A"
            value={trimA}
            onChange={setTrimA}
            disabled={!videoA}
            effective={playbackRate * (tempoMatch ? tempoMatch.rateA : 1) * trimA}
          />

          <span className="w-px h-5 bg-fairway-800 mx-1" />

          <TrimControl
            label="B"
            value={trimB}
            onChange={setTrimB}
            disabled={!videoB}
            effective={playbackRate * (tempoMatch ? tempoMatch.rateB : 1) * trimB}
          />

          <div className="flex-1" />
          <span className="hidden md:inline text-[10px] text-ink-500 italic">± 5% per click · resets with video</span>
        </div>

        <div className={`${showMore ? 'block' : 'hidden'} md:block`}>
          <DrawToolbar
            activeTool={activeTool}
            onToolChange={setActiveTool}
            activeColor={activeColor}
            onColorChange={setActiveColor}
          />
        </div>

        <div className={`${showMore ? 'flex' : 'hidden'} md:flex pt-2 border-t border-fairway-800 items-center gap-2 flex-wrap`}>
          <span className="text-[10px] uppercase tracking-[0.2em] text-ink-400 mr-1">Session</span>
          <input
            type="text"
            placeholder="Session name (e.g. Range — Oct 18, driver)"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            className="flex-1 min-w-[200px] px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 placeholder-ink-500 focus:outline-none focus:border-cream-300"
          />
          <button
            onClick={save}
            disabled={!canSave || saving}
            className="px-4 py-1.5 bg-fairway-600 text-ink-100 rounded text-xs uppercase tracking-wider hover:bg-fairway-500 disabled:opacity-30 transition"
          >
            {saving ? 'Saving...' : (loadedSessionId ? 'Update' : 'Save session')}
          </button>
          {saveMsg && <span className="text-[11px] text-cream-300">{saveMsg}</span>}
        </div>

        <div className="hidden md:flex text-[11px] text-ink-500 flex-wrap gap-x-4 gap-y-1 pt-2 border-t border-fairway-800">
          <span><span className="kbd">Space</span> play/pause</span>
          <span><span className="kbd">,</span> <span className="kbd">.</span> step frame</span>
          <span><span className="kbd">R</span> reset</span>
          <span><span className="kbd">1</span>–<span className="kbd">4</span> tools</span>
        </div>
      </div>

      {/* Video picker modal */}
      {pickerOpen && (
        <VideoPicker
          slot={pickerOpen}
          onClose={() => setPickerOpen(null)}
          onPick={(video) => {
            const v = { r2_key: video.r2_key, url: api.streamUrl(video.r2_key), label: video.label || video.player_name };
            if (pickerOpen === 'A') { setVideoA(v); setSyncA(null); setEndA(null); setDrawingsA([]); setTrimA(1); }
            else { setVideoB(v); setSyncB(null); setEndB(null); setDrawingsB([]); setTrimB(1); }
            setPickerOpen(null);
          }}
        />
      )}
    </div>
  );
}

function TrimControl({ label, value, onChange, disabled, effective }) {
  // 5% per click, clamped to a sane range so the video element doesn't choke
  const STEP = 0.05;
  const MIN = 0.25;
  const MAX = 2.0;
  const bump = (delta) => onChange(Math.min(MAX, Math.max(MIN, +(value + delta).toFixed(2))));
  const atMin = value <= MIN + 0.001;
  const atMax = value >= MAX - 0.001;
  const atDefault = Math.abs(value - 1) < 0.001;
  return (
    <div className="flex items-center gap-1">
      <span className="text-[11px] font-medium text-ink-300 mr-1">{label}</span>
      <button
        onClick={() => bump(-STEP)}
        disabled={disabled || atMin}
        title="Slower (−5%)"
        className="w-7 h-7 flex items-center justify-center rounded bg-fairway-800 hover:bg-fairway-700 text-ink-100 disabled:opacity-30 transition font-mono"
      >
        −
      </button>
      <span className="monospace text-[11px] text-ink-200 min-w-[44px] text-center tabular-nums">
        {value.toFixed(2)}×
      </span>
      <button
        onClick={() => bump(STEP)}
        disabled={disabled || atMax}
        title="Faster (+5%)"
        className="w-7 h-7 flex items-center justify-center rounded bg-fairway-800 hover:bg-fairway-700 text-ink-100 disabled:opacity-30 transition font-mono"
      >
        +
      </button>
      <button
        onClick={() => onChange(1)}
        disabled={disabled || atDefault}
        title="Reset to 1.00×"
        className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider text-ink-400 hover:text-ink-200 hover:bg-fairway-800 disabled:opacity-30 transition"
      >
        reset
      </button>
      {effective != null && !atDefault && (
        <span className="monospace text-[10px] text-ink-500">→ {effective.toFixed(2)}× live</span>
      )}
    </div>
  );
}

function EmptySlot({ label, onPick }) {
  return (
    <button
      onClick={onPick}
      style={{ maxWidth: 'calc((100vh - 360px) * 9 / 16)' }}
      className="w-full aspect-[9/16] mx-auto rounded-lg border-2 border-dashed border-fairway-700 bg-fairway-900/40 flex flex-col items-center justify-center gap-2 hover:border-cream-300 hover:bg-fairway-900/60 transition group"
    >
      <span className="display text-5xl text-ink-600 group-hover:text-ink-400 transition">+</span>
      <span className="text-[11px] uppercase tracking-[0.2em] text-ink-400">{label}</span>
      <span className="text-[11px] text-ink-500">Choose a video</span>
    </button>
  );
}

function SyncBadge({ status, syncA, syncB, tempoMatch }) {
  if (status === 'none') {
    return <span className="text-[10px] uppercase tracking-wider text-ink-500">Not synced</span>;
  }
  if (status === 'partial') {
    return (
      <span className="text-[10px] uppercase tracking-wider text-accent-marker">
        ● Sync partial
      </span>
    );
  }
  if (tempoMatch) {
    // Show the tempo ratio — e.g. A 1.40s : B 1.10s
    return (
      <span className="text-[10px] uppercase tracking-wider text-cream-300 monospace">
        ● Tempo matched · A {tempoMatch.windowA.toFixed(2)}s ∶ B {tempoMatch.windowB.toFixed(2)}s
      </span>
    );
  }
  return (
    <span className="text-[10px] uppercase tracking-wider text-cream-300 monospace">
      ● Synced · A@{syncA.toFixed(2)}s ↔ B@{syncB.toFixed(2)}s
    </span>
  );
}
